import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Music, Package, Users, CheckCircle, Bell, BarChart3,
  List, Layers, ShoppingCart, Truck, Headset, UserPlus,
  Smile, Globe, Crown, Mic, Send, Mail, Plus, Edit3,
  Trash2, Upload, RefreshCw, LogOut, AlertTriangle, X, Save, Eye, EyeOff, ListMusic, Music2, Image, Tag, Newspaper, Play, Pause, Check
} from 'lucide-react';

// ─── Full-access Panel Components ────────────────────
import OrdersPanel from '../components/panels/OrdersPanel';
import DeliveryPanel from '../components/panels/DeliveryPanel';
import LeadsPanel from '../components/panels/LeadsPanel';
import SatisfactionPanelShared from '../components/panels/SatisfactionPanelShared';
import GeographyPanel from '../components/panels/GeographyPanel';
import SupportPanel from '../components/panels/SupportPanel';
import ReplacementPanel from '../components/panels/ReplacementPanel';
import ProductionPanel from '../components/panels/ProductionPanel';
import MasteringStaffPanel from '../components/panels/MasteringStaffPanel';
import UltimateRequestsPanel from '../components/panels/UltimateRequestsPanel';
import NewsPanel from '../components/panels/NewsPanel';
import PlansPanel from '../components/panels/PlansPanel';
import ArtistsPanelFull from '../components/panels/ArtistsPanelFull';
import TracksPanelFull from '../components/panels/TracksPanelFull';
import PacksPanelFull from '../components/panels/PacksPanelFull';
import CampaignsPanelV2 from '../components/CampaignsPanelV2';
import MarketingStudio from '../components/MarketingStudio';
import PromotionsPanel from '../components/PromotionsPanel';
import EmailControlPanel from '../components/EmailControlPanel';
import GenreFilterBar from '../components/GenreFilterBar';
import { categoryForGenre } from '../utils/genreCategories';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── Status Badge ────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    draft: { label: 'Borrador', bg: '#333', color: '#aaa' },
    pending: { label: 'En espera', bg: '#3d2800', color: '#E8720C' },
    approved: { label: 'Aprobado', bg: '#0a2e0a', color: '#4ade80' },
    rejected: { label: 'Rechazado', bg: '#2e0a0a', color: '#f87171' },
    edited_approved: { label: 'Editado y Aprobado', bg: '#0a2e0a', color: '#4ade80' },
  };
  const s = map[status] || map.draft;
  return (
    <span data-testid={`status-badge-${status}`} style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, letterSpacing: 0.5
    }}>{s.label}</span>
  );
};

// ─── Track Form Modal ────────────────────────────────
const TrackModal = ({ track, onClose, onSave, artists }) => {
  const [form, setForm] = useState({
    title: track?.title || '', artist: track?.artist || '', genre: track?.genre || '',
    bpm: track?.bpm || '', key: track?.key || '', price: track?.price || '',
    cover_image: track?.cover_image || '', audio_preview_url: track?.audio_preview_url || '',
    wav_file_url: track?.wav_file_url || '', license_type: track?.license_type || 'standard',
    includes_playlist: track?.includes_playlist || false,
  });
  const [uploading, setUploading] = useState('');
  const [mp3Name, setMp3Name] = useState('');
  const [wavName, setWavName] = useState('');
  const [nameError, setNameError] = useState('');

  const getBaseName = (filename) => filename.replace(/\.[^/.]+$/, '').trim().toLowerCase();

  const validateNames = (newMp3, newWav) => {
    if (newMp3 && newWav && getBaseName(newMp3) !== getBaseName(newWav)) {
      setNameError(`Los nombres no coinciden: "${newMp3}" vs "${newWav}". Deben tener el mismo nombre.`);
    } else {
      setNameError('');
    }
  };

  const handleUpload = async (field, fileType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = field === 'wav_file_url' ? '.wav,audio/wav' : '.mp3,audio/mpeg';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      // Track filename
      if (field === 'audio_preview_url') {
        setMp3Name(file.name);
        validateNames(file.name, wavName);
      } else {
        setWavName(file.name);
        validateNames(mp3Name, file.name);
      }
      setUploading(field);
      const fd = new FormData();
      fd.append('file', file);
      try {
        const r = await axios.post(`${API_URL}/api/assistant/upload/${fileType}`, fd, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
        setForm(p => ({ ...p, [field]: r.data.url }));
      } catch (err) {
        alert(err.response?.data?.detail || 'Error subiendo archivo');
      }
      setUploading('');
    };
    input.click();
  };

  const handleSubmit = () => {
    if (!form.title) return alert('Titulo es requerido');
    onSave({ ...form, bpm: form.bpm ? parseInt(form.bpm) : null, price: parseFloat(form.price) || 0 });
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', background: '#1a1a1e', border: '1px solid #333',
    borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none',
  };
  const labelStyle = { color: '#999', fontSize: 12, marginBottom: 4, display: 'block', fontWeight: 500 };

  return (
    <div data-testid="track-modal-overlay" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div data-testid="track-modal" style={{
        background: '#141416', borderRadius: 16, padding: 32, width: 540, maxHeight: '85vh',
        overflowY: 'auto', border: '1px solid #2a2a2e',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: '#fff', fontSize: 20, marginBottom: 24 }}>
          {track ? 'Editar Track' : 'Nuevo Track'}
        </h3>
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={labelStyle}>Titulo *</label>
            <input data-testid="track-title-input" style={inputStyle} value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Nombre del beat" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Etiqueta / Tag (opcional)</label>
              <input data-testid="track-artist-input" style={inputStyle} value={form.artist}
                onChange={e => setForm(p => ({ ...p, artist: e.target.value }))} placeholder="Tag o referencia..." />
            </div>
            <div>
              <label style={labelStyle}>Genero</label>
              <input data-testid="track-genre-input" style={inputStyle} value={form.genre}
                onChange={e => setForm(p => ({ ...p, genre: e.target.value }))} placeholder="Reggaeton, Dembow..." />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>BPM</label>
              <input data-testid="track-bpm-input" style={inputStyle} type="number" value={form.bpm}
                onChange={e => setForm(p => ({ ...p, bpm: e.target.value }))} placeholder="120" />
            </div>
            <div>
              <label style={labelStyle}>Key</label>
              <input data-testid="track-key-input" style={inputStyle} value={form.key}
                onChange={e => setForm(p => ({ ...p, key: e.target.value }))} placeholder="Am, Cm..." />
            </div>
            <div>
              <label style={labelStyle}>Precio (USD)</label>
              <input data-testid="track-price-input" style={inputStyle} type="number" step="0.01" value={form.price}
                onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="29.99" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Audio MP3 *</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {form.audio_preview_url && <span style={{ color: '#4ade80', fontSize: 12 }}>MP3 subido</span>}
              {mp3Name && <span style={{ color: '#888', fontSize: 10 }}>{mp3Name}</span>}
              <button data-testid="upload-mp3-btn" onClick={() => handleUpload('audio_preview_url', 'audio')}
                disabled={!!uploading} style={{
                  padding: '8px 16px', background: '#1a1a1e', border: '1px solid #444',
                  borderRadius: 8, color: '#E8720C', cursor: 'pointer', fontSize: 13,
                }}>{uploading === 'audio_preview_url' ? 'Subiendo...' : 'Subir MP3'}</button>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Archivo WAV *</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {form.wav_file_url && <span style={{ color: '#4ade80', fontSize: 12 }}>WAV subido</span>}
              {wavName && <span style={{ color: '#888', fontSize: 10 }}>{wavName}</span>}
              <button data-testid="upload-wav-btn" onClick={() => handleUpload('wav_file_url', 'audio')}
                disabled={!!uploading} style={{
                  padding: '8px 16px', background: '#1a1a1e', border: '1px solid #444',
                  borderRadius: 8, color: '#E8720C', cursor: 'pointer', fontSize: 13,
                }}>{uploading === 'wav_file_url' ? 'Subiendo...' : 'Subir WAV'}</button>
            </div>
          </div>
          {/* Name match validation */}
          {nameError && (
            <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8 }}>
              <p style={{ color: '#ef4444', fontSize: 12, margin: 0 }}>{nameError}</p>
            </div>
          )}
          {mp3Name && wavName && !nameError && (
            <div style={{ padding: '8px 12px', background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 8 }}>
              <p style={{ color: '#4ade80', fontSize: 12, margin: 0 }}>Nombres coinciden correctamente</p>
            </div>
          )}
          {/* Playlist Ecosystem Toggle */}
          <div>
            <button data-testid="track-playlist-toggle" onClick={() => setForm(p => ({ ...p, includes_playlist: !p.includes_playlist }))}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 10, border: 'none',
                background: form.includes_playlist ? 'rgba(232,114,12,0.15)' : '#1a1a1e',
                color: form.includes_playlist ? '#E8720C' : '#666',
                outline: form.includes_playlist ? '1px solid rgba(232,114,12,0.3)' : '1px solid #333',
              }}>
              <span style={{ width: 18, height: 18, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: form.includes_playlist ? '#E8720C' : '#333', color: '#fff', fontSize: 11 }}>
                {form.includes_playlist ? 'v' : ''}
              </span>
              Incluye Ecosistema de Playlists
            </button>
            {form.includes_playlist && (
              <p style={{ color: '#888', fontSize: 11, marginTop: 6 }}>Este track tendra un precio mayor. El comprador podra enviarnos su link para ser incluido en nuestras playlists.</p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
          <button data-testid="track-cancel-btn" onClick={onClose} style={{
            padding: '10px 24px', background: '#333', border: 'none', borderRadius: 8,
            color: '#aaa', cursor: 'pointer', fontSize: 14,
          }}>Cancelar</button>
          <button data-testid="track-save-btn" onClick={handleSubmit} style={{
            padding: '10px 24px', background: '#E8720C', border: 'none', borderRadius: 8,
            color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}>Guardar</button>
        </div>
      </div>
    </div>
  );
};

// ─── Artist Modal ────────────────────────────────────
const ArtistModal = ({ artist, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: artist?.name || '', bio: artist?.bio || '',
    image_url: artist?.image_url || '', genres: artist?.genres?.join(', ') || '',
  });
  const [uploading, setUploading] = useState(false);

  const handleUploadImage = async () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      const fd = new FormData(); fd.append('file', file);
      try {
        const r = await axios.post(`${API_URL}/api/assistant/upload/images`, fd, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
        setForm(p => ({ ...p, image_url: r.data.url }));
      } catch { alert('Error subiendo imagen'); }
      setUploading(false);
    };
    input.click();
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', background: '#1a1a1e', border: '1px solid #333',
    borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div data-testid="artist-modal" style={{
        background: '#141416', borderRadius: 16, padding: 32, width: 480, border: '1px solid #2a2a2e',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: '#fff', fontSize: 20, marginBottom: 24 }}>
          {artist ? 'Editar Artista' : 'Nuevo Artista'}
        </h3>
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Nombre *</label>
            <input data-testid="artist-name-input" style={inputStyle} value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <label style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Bio</label>
            <textarea data-testid="artist-bio-input" style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
              value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} />
          </div>
          <div>
            <label style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Generos (separados por coma)</label>
            <input data-testid="artist-genres-input" style={inputStyle} value={form.genres}
              onChange={e => setForm(p => ({ ...p, genres: e.target.value }))} placeholder="Reggaeton, Dembow, Trap" />
          </div>
          <div>
            <label style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Imagen</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {form.image_url && <img src={form.image_url.startsWith('/') ? `${API_URL}${form.image_url}` : form.image_url}
                alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />}
              <button data-testid="artist-upload-img-btn" onClick={handleUploadImage} disabled={uploading} style={{
                padding: '8px 16px', background: '#1a1a1e', border: '1px solid #444',
                borderRadius: 8, color: '#E8720C', cursor: 'pointer', fontSize: 13,
              }}>{uploading ? 'Subiendo...' : 'Subir Imagen'}</button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
          <button onClick={onClose} style={{
            padding: '10px 24px', background: '#333', border: 'none', borderRadius: 8,
            color: '#aaa', cursor: 'pointer', fontSize: 14,
          }}>Cancelar</button>
          <button data-testid="artist-save-btn" onClick={() => {
            if (!form.name) return alert('Nombre requerido');
            onSave({ ...form, genres: form.genres.split(',').map(g => g.trim()).filter(Boolean) });
          }} style={{
            padding: '10px 24px', background: '#E8720C', border: 'none', borderRadius: 8,
            color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}>Guardar</button>
        </div>
      </div>
    </div>
  );
};


// ─── Pack Form Modal (Assistant) ─────────────────────
const PackModalAssistant = ({ pack, tracks, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: pack?.name || '', description: pack?.description || '', genre: pack?.genre || '',
    mood: pack?.mood || '', track_count: pack?.track_count || 0, price: pack?.price || '',
    track_ids: pack?.track_ids || [], includes_playlist: pack?.includes_playlist || false,
  });
  const [trackSearch, setTrackSearch] = useState('');
  const [showTrackPicker, setShowTrackPicker] = useState(false);

  const addTrack = (id) => setForm(p => ({ ...p, track_ids: [...p.track_ids, id], track_count: p.track_ids.length + 1 }));
  const removeTrack = (id) => setForm(p => ({ ...p, track_ids: p.track_ids.filter(x => x !== id), track_count: Math.max(0, p.track_ids.length - 1) }));

  const handleSubmit = () => {
    if (!form.name) return alert('Nombre es requerido');
    onSave({ ...form, track_count: parseInt(form.track_count) || form.track_ids.length, price: parseFloat(form.price) || 0, includes_playlist: form.includes_playlist });
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', background: '#1a1a1e', border: '1px solid #333',
    borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none',
  };
  const labelStyle = { color: '#999', fontSize: 12, marginBottom: 4, display: 'block', fontWeight: 500 };

  return (
    <div data-testid="pack-modal-overlay" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div data-testid="pack-modal" style={{
        background: '#141416', borderRadius: 16, padding: 32, width: 560, maxHeight: '85vh',
        overflowY: 'auto', border: '1px solid #2a2a2e',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: '#fff', fontSize: 20, marginBottom: 24 }}>
          {pack ? 'Editar Paquete' : 'Nuevo Paquete'}
        </h3>
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={labelStyle}>Nombre del Paquete *</label>
            <input data-testid="pack-name-input" style={inputStyle} value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej: Latin Vibes Vol. 1" />
          </div>
          <div>
            <label style={labelStyle}>Descripcion</label>
            <textarea data-testid="pack-desc-input" style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
              value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Genero</label>
              <input data-testid="pack-genre-input" style={inputStyle} value={form.genre}
                onChange={e => setForm(p => ({ ...p, genre: e.target.value }))} placeholder="Reggaeton..." />
            </div>
            <div>
              <label style={labelStyle}>Mood / Vibe</label>
              <input data-testid="pack-mood-input" style={inputStyle} value={form.mood}
                onChange={e => setForm(p => ({ ...p, mood: e.target.value }))} placeholder="Dark, Happy..." />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Cantidad de Tracks</label>
              <input data-testid="pack-count-input" type="number" style={inputStyle} value={form.track_count}
                onChange={e => setForm(p => ({ ...p, track_count: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Precio (USD)</label>
              <input data-testid="pack-price-input" type="number" step="0.01" style={inputStyle} value={form.price}
                onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="29.99" />
            </div>
          </div>
          {/* Track selection */}
          <div>
            <label style={labelStyle}>Tracks en este Pack ({form.track_ids.length} seleccionados)</label>
            {form.track_ids.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {form.track_ids.map(tid => {
                  const tr = (tracks || []).find(t => t.id === tid);
                  return tr ? (
                    <span key={tid} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: 'rgba(232,114,12,0.15)', border: '1px solid rgba(232,114,12,0.3)', borderRadius: 20, fontSize: 12, color: '#E8720C' }}>
                      {tr.title}
                      <button onClick={() => removeTrack(tid)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 14 }}>x</button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
            <button type="button" onClick={() => setShowTrackPicker(true)}
              style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #333', borderRadius: 12, color: '#E8720C', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              data-testid="open-track-picker">
              + Seleccionar Tracks
            </button>
          </div>

          {/* Full-screen Track Picker */}
          {showTrackPicker && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 60, display: 'flex', flexDirection: 'column' }} data-testid="track-picker-modal"
              onKeyDown={e => { if (e.key === 'Escape') { setShowTrackPicker(false); setTrackSearch(''); } }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #1a1a1e', background: '#0a0a0e', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <button onClick={() => { setShowTrackPicker(false); setTrackSearch(''); }}
                    style={{ padding: 8, borderRadius: 8, background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 18 }}>X</button>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff' }}>Seleccionar Tracks</h3>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#E8720C' }}>{form.track_ids.length} seleccionados</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input style={{ ...inputStyle, width: 280, fontSize: 13 }} placeholder="Buscar por nombre o artista..."
                    value={trackSearch} onChange={e => setTrackSearch(e.target.value)} autoFocus />
                  <button onClick={() => { setShowTrackPicker(false); setTrackSearch(''); }}
                    style={{ padding: '10px 24px', background: '#E8720C', borderRadius: 12, border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                    Listo ({form.track_ids.length})
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {(() => {
                  const filtered = (tracks || []).filter(t => !trackSearch || t.title?.toLowerCase().includes(trackSearch.toLowerCase()) || (t.artist||'').toLowerCase().includes(trackSearch.toLowerCase()));
                  const genres = {};
                  filtered.forEach(t => { const g = t.genre || 'Sin Genero'; if (!genres[g]) genres[g] = []; genres[g].push(t); });
                  return Object.keys(genres).sort().map(genre => (
                    <div key={genre}>
                      <div style={{ position: 'sticky', top: 0, zIndex: 10, padding: '10px 24px', background: '#111114', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#E8720C' }}>{genre}</span>
                          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>{genres[genre].length} tracks</span>
                        </div>
                        <button type="button" onClick={() => {
                          const ids = genres[genre].map(t => t.id);
                          const allSel = ids.every(id => form.track_ids.includes(id));
                          if (allSel) { ids.forEach(id => removeTrack(id)); } else { ids.forEach(id => { if (!form.track_ids.includes(id)) addTrack(id); }); }
                        }} style={{ fontSize: 10, padding: '4px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                          {genres[genre].every(t => form.track_ids.includes(t.id)) ? 'Deseleccionar todo' : 'Seleccionar todo'}
                        </button>
                      </div>
                      {genres[genre].map((t, i) => {
                        const isSel = form.track_ids.includes(t.id);
                        return (
                          <div key={t.id} onClick={() => isSel ? removeTrack(t.id) : addTrack(t.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 16, padding: '14px 24px', cursor: 'pointer',
                              borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'background 0.15s',
                              background: isSel ? 'rgba(232,114,12,0.1)' : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                            }}
                            onMouseOver={e => { if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                            onMouseOut={e => { if (!isSel) e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'; }}>
                            <div style={{
                              width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              background: isSel ? '#E8720C' : 'transparent', border: isSel ? 'none' : '2px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: 14,
                            }}>
                              {isSel && 'v'}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: 14, fontWeight: 600, color: isSel ? '#E8720C' : 'rgba(255,255,255,0.8)' }}>{t.title}</span>
                              <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)', marginLeft: 12 }}>{t.artist}</span>
                            </div>
                            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', width: 60, textAlign: 'right', flexShrink: 0 }}>{t.bpm > 0 ? `${t.bpm} BPM` : ''}</span>
                            <span style={{ fontSize: 12, color: '#E8720C', width: 60, textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>${t.price}</span>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
          {/* Playlist Toggle */}
          <div>
            <button data-testid="pack-playlist-toggle" onClick={() => setForm(p => ({ ...p, includes_playlist: !p.includes_playlist }))}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 10, border: 'none',
                background: form.includes_playlist ? 'rgba(232,114,12,0.15)' : '#1a1a1e',
                color: form.includes_playlist ? '#E8720C' : '#666',
                outline: form.includes_playlist ? '1px solid rgba(232,114,12,0.3)' : '1px solid #333',
              }}>
              <span style={{ width: 18, height: 18, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: form.includes_playlist ? '#E8720C' : '#333', color: '#fff', fontSize: 11 }}>
                {form.includes_playlist ? 'v' : ''}
              </span>
              Incluye Ecosistema de Playlists
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
          <button data-testid="pack-cancel-btn" onClick={onClose} style={{
            padding: '10px 24px', background: '#333', border: 'none', borderRadius: 8,
            color: '#aaa', cursor: 'pointer', fontSize: 14,
          }}>Cancelar</button>
          <button data-testid="pack-save-btn" onClick={handleSubmit} style={{
            padding: '10px 24px', background: '#E8720C', border: 'none', borderRadius: 8,
            color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}>Guardar Paquete</button>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════
// ─── MAIN ASSISTANT PAGE ────────────────────────────
// ═══════════════════════════════════════════════════════
const AssistantPage = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('tracks');
  const [asstCampSubTab, setAsstCampSubTab] = useState('promotions');
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [editTrack, setEditTrack] = useState(null);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [editArtist, setEditArtist] = useState(null);
  const [selectedTracks, setSelectedTracks] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [buyerHistory, setBuyerHistory] = useState(null);
  const [buyerHistoryLoading, setBuyerHistoryLoading] = useState(false);
  const [packs, setPacks] = useState([]);
  const [showPackModal, setShowPackModal] = useState(false);
  const [editPack, setEditPack] = useState(null);
  const [selectedPacks, setSelectedPacks] = useState(new Set());
  const [capacity, setCapacity] = useState({ visible: 0, limit: 1000, remaining: 1000, is_full: false });
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploaded, setBulkUploaded] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [batchSettings, setBatchSettings] = useState({ bpm: '', price: '', genre: '', includes_playlist: false });
  const [uploadingBatchCover, setUploadingBatchCover] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const [spotifyPlaylists, setSpotifyPlaylists] = useState([]);
  const [spModal, setSpModal] = useState(null);
  // Audio preview + inline genre edit
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const audioPlayerRef = React.useRef(null);
  const [editingGenreId, setEditingGenreId] = useState(null);
  const [genreDraft, setGenreDraft] = useState('');
  // Genre category filter
  const [genreCategory, setGenreCategory] = useState(null);
  const [genreRaw, setGenreRaw] = useState(null);

  // ── Data fetchers ──
  const loadSupportBadge = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/chat-sessions`, { withCredentials: true });
      const total = (r.data || []).reduce((s, c) => s + (c.unread || 0), 0);
      setSupportUnread(total);
    } catch { }
  }, []);

  const loadCapacity = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assistant/track-capacity`, { withCredentials: true });
      setCapacity(r.data);
    } catch { }
  }, []);

  const loadTracks = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assistant/tracks`, { withCredentials: true });
      setTracks(r.data);
    } catch { }
  }, []);

  const loadArtists = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assistant/artists`, { withCredentials: true });
      setArtists(r.data);
    } catch { }
  }, []);

  const loadApprovals = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assistant/approvals`, { withCredentials: true });
      setApprovals(r.data);
    } catch { }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assistant/notifications`, { withCredentials: true });
      setNotifications(r.data.filter(n => !n.dismissed));
    } catch { }
  }, []);

  const loadMetrics = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assistant/metrics`, { withCredentials: true });
      setMetrics(r.data);
    } catch { }
  }, []);

  const loadPacks = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assistant/packs`, { withCredentials: true });
      setPacks(r.data);
    } catch { }
  }, []);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assistant/notifications/history`, { withCredentials: true });
      setHistory(r.data);
      setShowHistory(true);
    } catch { }
  };

  const handleNotifClick = async (n) => {
    setSelectedNotif(n);
    if (!n.read) {
      axios.post(`${API_URL}/api/assistant/notifications/${n.id}/read`, {}, { withCredentials: true }).catch(() => {});
    }
    // Fetch buyer history for playlist notifications
    if ((n.type === 'playlist_submission' || (n.title || '').toLowerCase().includes('playlist')) && n.data?.user_email) {
      setBuyerHistory(null);
      setBuyerHistoryLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin/buyer-history?email=${encodeURIComponent(n.data.user_email)}`, { withCredentials: true });
        setBuyerHistory(r.data);
      } catch { }
      setBuyerHistoryLoading(false);
    } else {
      setBuyerHistory(null);
    }
  };

  useEffect(() => {
    loadTracks(); loadArtists(); loadApprovals(); loadNotifications(); loadMetrics(); loadPacks(); loadCapacity(); loadSupportBadge(); loadSpotifyPlaylists();
  }, [loadTracks, loadArtists, loadApprovals, loadNotifications, loadMetrics, loadPacks, loadCapacity, loadSupportBadge]);

  useEffect(() => {
    // Wait for auth check to complete before redirecting
    if (!authLoading && (!user || user.role !== 'assistant')) navigate('/login');
  }, [user, navigate, authLoading]);

  // ── Track actions ──
  const handleSaveTrack = async (data) => {
    setLoading(true);
    try {
      if (editTrack) {
        await axios.put(`${API_URL}/api/assistant/tracks/${editTrack.id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/assistant/tracks`, data, { withCredentials: true });
      }
      setShowTrackModal(false); setEditTrack(null);
      await loadTracks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error guardando track');
    }
    setLoading(false);
  };

  const handleDeleteTrack = async (id) => {
    if (!window.confirm('Eliminar este track?')) return;
    try {
      await axios.delete(`${API_URL}/api/assistant/tracks/${id}`, { withCredentials: true });
      await loadTracks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error eliminando track');
    }
  };

  // --- Play preview to verify genre ---
  const togglePlayTrack = (track) => {
    const url = track.audio_preview_url || track.wav_file_url;
    if (!url) { alert('Este track no tiene audio disponible'); return; }
    if (playingTrackId === track.id) {
      try { audioPlayerRef.current?.pause(); } catch {}
      setPlayingTrackId(null);
      return;
    }
    if (!audioPlayerRef.current) {
      const el = new Audio();
      el.preload = 'auto';
      el.onended = () => setPlayingTrackId(null);
      el.onerror = () => { setPlayingTrackId(null); alert('Error reproduciendo el audio'); };
      audioPlayerRef.current = el;
    }
    try { audioPlayerRef.current.pause(); } catch {}
    audioPlayerRef.current.src = url;
    audioPlayerRef.current.currentTime = 0;
    audioPlayerRef.current.play().then(() => setPlayingTrackId(track.id))
      .catch(() => { setPlayingTrackId(null); alert('No se pudo reproducir'); });
  };

  useEffect(() => () => { try { audioPlayerRef.current?.pause(); } catch {} }, []);

  // --- Inline genre edit ---
  const startEditGenre = (track) => { setEditingGenreId(track.id); setGenreDraft(track.genre || ''); };
  const cancelEditGenre = () => { setEditingGenreId(null); setGenreDraft(''); };
  const saveGenre = async (track) => {
    const nextGenre = (genreDraft || '').trim();
    if (!nextGenre || nextGenre === track.genre) { cancelEditGenre(); return; }
    try {
      // Send only the genre — backend allows genre-only edits on approved tracks
      await axios.put(`${API_URL}/api/assistant/tracks/${track.id}`, { genre: nextGenre }, { withCredentials: true });
      cancelEditGenre();
      await loadTracks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error actualizando género');
    }
  };

  const handleSubmitApproval = async () => {
    if (selectedTracks.size === 0) return alert('Selecciona al menos un track');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/assistant/tracks/submit-approval`, {
        track_ids: Array.from(selectedTracks)
      }, { withCredentials: true });
      setSelectedTracks(new Set());
      await Promise.all([loadTracks(), loadApprovals()]);
      alert('Tracks enviados para aprobacion');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error enviando aprobacion');
    }
    setLoading(false);
  };

  // ── Artist actions ──
  const handleSaveArtist = async (data) => {
    try {
      if (editArtist) {
        await axios.put(`${API_URL}/api/assistant/artists/${editArtist.id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/assistant/artists`, data, { withCredentials: true });
      }
      setShowArtistModal(false); setEditArtist(null);
      await loadArtists();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error guardando artista');
    }
  };

  // ── Pack actions ──
  const handleSavePack = async (data) => {
    setLoading(true);
    try {
      if (editPack) {
        await axios.put(`${API_URL}/api/assistant/packs/${editPack.id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/assistant/packs`, data, { withCredentials: true });
      }
      setShowPackModal(false); setEditPack(null);
      await loadPacks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error guardando paquete');
    }
    setLoading(false);
  };

  const handleDeletePack = async (id) => {
    if (!window.confirm('Eliminar este paquete?')) return;
    try {
      await axios.delete(`${API_URL}/api/assistant/packs/${id}`, { withCredentials: true });
      await loadPacks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error eliminando paquete');
    }
  };

  const handleSubmitPackApproval = async () => {
    if (selectedPacks.size === 0) return alert('Selecciona al menos un paquete');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/assistant/packs/submit-approval`, {
        pack_ids: Array.from(selectedPacks)
      }, { withCredentials: true });
      setSelectedPacks(new Set());
      await Promise.all([loadPacks(), loadApprovals()]);
      alert('Paquetes enviados para aprobacion');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error enviando aprobacion');
    }
    setLoading(false);
  };

  // ── Notification actions ──
  const handleDismissNotifs = async (ids) => {
    try {
      await axios.post(`${API_URL}/api/assistant/notifications/dismiss`, { ids }, { withCredentials: true });
      await loadNotifications();
    } catch { }
  };

  const toggleTrackSelection = (id) => {
    setSelectedTracks(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleLogout = async () => {
    try { await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch { }
    logout();
    navigate('/login');
  };

  // ── Bulk Upload ──
  const handleBulkUpload = async () => {
    if (!bulkFiles.length) return;
    setBulkUploading(true);
    const allUploaded = [];
    const allErrors = [];
    setBulkProgress({ current: 0, total: bulkFiles.length });

    // Upload ONE file at a time (bypasses ingress body size limits & most reliable)
    for (let i = 0; i < bulkFiles.length; i++) {
      const file = bulkFiles[i];
      const fd = new FormData();
      fd.append('files', file);
      try {
        // Do NOT set Content-Type manually — axios auto-sets multipart boundary for FormData
        const r = await axios.post(`${API_URL}/api/assistant/tracks/bulk-upload`, fd, {
          withCredentials: true,
          timeout: 180000,
        });
        if (r.data.uploaded) allUploaded.push(...r.data.uploaded);
        if (r.data.errors) allErrors.push(...r.data.errors);
      } catch (err) {
        console.error('Assistant bulk upload failed for', file.name, err.response?.status, err.response?.data, err.message);
        allErrors.push({ filename: file.name, error: err.response?.data?.detail || err.message || 'Error de conexion' });
      }
      setBulkProgress({ current: i + 1, total: bulkFiles.length });
    }

    const getBase = (n) => (n || '').replace(/\.[^/.]+$/, '').trim().toLowerCase();
    const mp3Results = allUploaded.filter(u => (u.format || '').toUpperCase() === 'MP3');
    const wavResults = allUploaded.filter(u => (u.format || '').toUpperCase() === 'WAV');

    const paired = [];
    mp3Results.forEach(mp3 => {
      const base = getBase(mp3.original_filename || mp3.filename);
      const wavMatch = wavResults.find(w => getBase(w.original_filename || w.filename) === base);
      paired.push({
        ...mp3,
        title: mp3.suggested_title || base,
        bpm: batchSettings.bpm || '',
        price: batchSettings.price || '',
        genre: batchSettings.genre || '',
        includes_playlist: batchSettings.includes_playlist || false,
        wav_url: wavMatch ? wavMatch.url : '',
      });
    });
    wavResults.forEach(wav => {
      const base = getBase(wav.original_filename || wav.filename);
      if (!mp3Results.some(m => getBase(m.original_filename || m.filename) === base)) {
        paired.push({ ...wav, title: wav.suggested_title || base, bpm: batchSettings.bpm || '', price: batchSettings.price || '', genre: batchSettings.genre || '', includes_playlist: batchSettings.includes_playlist || false, wav_url: wav.url, url: '' });
      }
    });

    setBulkUploaded(paired);
    setBulkFiles([]);
    setBulkProgress({ current: 0, total: 0 });
    if (allErrors.length) alert(`${allUploaded.length} subidos correctamente. ${allErrors.length} con error.`);
    setBulkUploading(false);
  };

  const updateBulkTrack = (idx, field, value) => {
    setBulkUploaded(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const applyBatchSettings = () => {
    setBulkUploaded(prev => prev.map(t => ({
      ...t,
      bpm: batchSettings.bpm || t.bpm,
      price: batchSettings.price || t.price,
      genre: batchSettings.genre || t.genre,
      includes_playlist: batchSettings.includes_playlist,
    })));
  };

  const handleBulkCreate = async () => {
    setBulkCreating(true);
    try {
      const r = await axios.post(`${API_URL}/api/assistant/tracks/bulk-create`, {
        tracks: bulkUploaded.map(t => ({
          title: t.title, artist: t.artist || '', genre: t.genre || '', bpm: t.bpm || null,
          price: t.price || 0, audio_preview_url: t.url || '',
          preview_url: t.preview_url || '',
          wav_file_url: t.wav_url || '',
          includes_playlist: t.includes_playlist || false,
          original_filename: t.original_filename,
        }))
      }, { withCredentials: true });
      alert(`${r.data.total} tracks creados!`);
      setBulkUploaded([]);
      setBatchSettings({ bpm: '', price: '', genre: '', includes_playlist: false });
      await Promise.all([loadTracks(), loadCapacity()]);
    } catch (err) { alert(err.response?.data?.detail || 'Error creando tracks'); }
    setBulkCreating(false);
  };

  const handleBatchCoverUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0]; if (!file) return;
      setUploadingBatchCover(true);
      const fd = new FormData(); fd.append('file', file);
      try {
        const r = await axios.post(`${API_URL}/api/assistant/upload/images`, fd, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
        setBatchSettings(p => ({ ...p, cover_image: r.data.url }));
      } catch { alert('Error subiendo portada'); }
      setUploadingBatchCover(false);
    };
    input.click();
  };

  // ── Spotify Playlists ──
  const loadSpotifyPlaylists = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/spotify-playlists`, { withCredentials: true });
      setSpotifyPlaylists(r.data || []);
    } catch {}
  };
  const handleSaveSpotifyPlaylist = async (data) => {
    try {
      if (data.id) {
        await axios.put(`${API_URL}/api/admin/spotify-playlists/${data.id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/spotify-playlists`, data, { withCredentials: true });
      }
      setSpModal(null);
      loadSpotifyPlaylists();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
  };
  const handleDeleteSpotifyPlaylist = async (id) => {
    if (!window.confirm('Eliminar esta playlist?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/spotify-playlists/${id}`, { withCredentials: true });
      loadSpotifyPlaylists();
    } catch { alert('Error eliminando'); }
  };

  // ── Sidebar tabs ──
  const tabs = [
    { id: 'tracks', label: 'Mis Tracks', icon: Music },
    { id: 'packs', label: 'Mis Paquetes', icon: Package },
    { id: 'artists', label: 'Artistas', icon: Users },
    { id: 'spotify-playlists', label: 'Spotify Playlists', icon: ListMusic },
    { id: 'approvals', label: 'Aprobaciones', icon: CheckCircle },
    { id: 'notifications', label: 'Notificaciones', icon: Bell, badge: notifications.filter(n => !n.read).length },
    { id: 'metrics', label: 'Mis Metricas', icon: BarChart3 },
    { id: 'divider1', label: 'Modulos Completos', icon: null, divider: true },
    { id: 'f-tracks', label: 'Tracks (Catalogo)', icon: List },
    { id: 'f-packs', label: 'Packs (Catalogo)', icon: Layers },
    { id: 'f-orders', label: 'Ordenes', icon: ShoppingCart },
    { id: 'f-deliveries', label: 'Entregas', icon: Truck },
    { id: 'f-support', label: 'Soporte', icon: Headset, badge: supportUnread },
    { id: 'f-replacements', label: 'Reemplazos', icon: RefreshCw },
    { id: 'f-production', label: 'Produccion', icon: Music2 },
    { id: 'f-mastering', label: 'Mastering Clientes', icon: Music2 },
    { id: 'f-ultimate', label: 'Ultimate Pack', icon: Crown },
    { id: 'f-news', label: 'Noticias', icon: Newspaper },
    { id: 'f-leads', label: 'Leads', icon: UserPlus },
    { id: 'f-satisfaction', label: 'Satisfaccion', icon: Smile },
    { id: 'f-geography', label: 'Geografia', icon: Globe },
    { id: 'f-plans', label: 'Planes', icon: Crown },
    { id: 'f-campaigns', label: 'Campanas', icon: Send },
    { id: 'f-email', label: 'Email Control', icon: Mail },
  ];

  if (authLoading) {
    return (
      <div className="flex min-h-screen bg-[#0a0a0c] items-center justify-center">
        <div className="text-white/40">Cargando...</div>
      </div>
    );
  }

  if (!user || user.role !== 'assistant') return null;

  return (
    <div data-testid="assistant-page" className="flex h-screen bg-[#0a0a0c]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Sidebar — Admin Style */}
      <aside data-testid="assistant-sidebar" className="w-[260px] bg-[#111114] border-r border-white/5 flex flex-col h-screen overflow-hidden shrink-0">
        <div className="p-5 pb-4 border-b border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC" className="h-8 opacity-90" style={{ filter: 'drop-shadow(0 0 8px rgba(232,114,12,0.15))' }} />
          </div>
          <div className="text-white/40 text-[10px] tracking-[0.2em] uppercase font-medium">Panel Asistente</div>
          <div className="text-white/50 text-xs mt-1">{user.name}</div>
          {/* Capacity Bar */}
          <div className="mt-3 p-2 rounded-lg bg-white/[0.03] border border-white/5">
            <div className="flex justify-between text-[10px] mb-1">
              <span className="text-white/40">Capacidad</span>
              <span className={`font-bold ${capacity.is_full ? 'text-red-400' : capacity.remaining < 100 ? 'text-yellow-400' : 'text-green-400'}`}>
                {capacity.visible}/{capacity.limit}
              </span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${capacity.is_full ? 'bg-red-500' : capacity.remaining < 100 ? 'bg-yellow-500' : 'bg-[#E8720C]'}`}
                style={{ width: `${Math.min(100, (capacity.visible / capacity.limit) * 100)}%` }} />
            </div>
            <div className="text-[9px] text-white/30 mt-1">{capacity.remaining} slots disponibles</div>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {tabs.map(t => t.divider ? (
            <div key={t.id} className="px-5 pt-4 pb-1 text-[9px] font-semibold tracking-widest uppercase text-white/20">
              {t.label}
            </div>
          ) : (
            <button key={t.id} data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-3 w-full px-5 py-2.5 text-[13px] transition-all border-l-[3px] ${
                tab === t.id
                  ? 'bg-[#E8720C]/10 text-[#E8720C] border-[#E8720C]'
                  : 'text-white/40 border-transparent hover:bg-white/[0.03] hover:text-white/60'
              }`}>
              {t.icon && <t.icon className="w-4 h-4 shrink-0" />}
              <span className="truncate">{t.label}</span>
              {t.badge > 0 && (
                <span className="ml-auto w-5 h-5 bg-[#E8720C] rounded-full flex items-center justify-center text-[10px] font-bold text-white">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <button data-testid="assistant-logout-btn" onClick={handleLogout}
            className="w-full py-2 bg-white/[0.03] border border-white/5 rounded-lg text-red-400 hover:bg-red-500/10 text-xs flex items-center justify-center gap-2 transition-colors">
            <LogOut className="w-3.5 h-3.5" />Cerrar Sesion
          </button>
        </div>
      </aside>

      {/* Main Content — Admin Style */}
      <main className="flex-1 p-8 overflow-y-auto h-screen">

        {/* Full capacity warning */}
        {capacity.is_full && (
          <div className="glass-card p-4 mb-6 border border-red-500/30 bg-red-500/5" data-testid="capacity-full-alert">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-sm font-medium text-red-400">Capacidad completa ({capacity.limit} tracks)</p>
                <p className="text-xs text-white/50">Ya estamos llenos. Sigue haciendo canciones y guardalas localmente en la carpeta de musica lista para subir.</p>
              </div>
            </div>
          </div>
        )}

        {/* ── TRACKS TAB (Personal + Bulk Upload) ── */}
        {tab === 'tracks' && (
          <div data-testid="tracks-section">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold">Mis Tracks</h2>
                <p className="text-white/50 text-sm">Sube, edita y envia para aprobacion</p>
              </div>
              <div className="flex gap-3 items-center">
                {/* Capacity Badge */}
                <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  capacity.is_full ? 'bg-red-500/20 text-red-400' : capacity.remaining < 100 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'
                }`} data-testid="capacity-badge">
                  {capacity.visible}/{capacity.limit} tracks
                </span>
                {selectedTracks.size > 0 && (
                  <button data-testid="submit-approval-btn" onClick={handleSubmitApproval} disabled={loading}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-xl text-sm font-semibold text-white flex items-center gap-2">
                    <Send className="w-4 h-4" />Enviar {selectedTracks.size} a Aprobacion
                  </button>
                )}
                <label className="px-4 py-2 bg-white/10 hover:bg-white/15 rounded-xl text-sm text-white/70 flex items-center gap-2 cursor-pointer" data-testid="bulk-upload-btn">
                  <Upload className="w-4 h-4" /> Subida por Lote (MP3 + WAV)
                  <input type="file" multiple accept=".mp3,.wav,audio/mpeg,audio/wav" className="hidden"
                    onChange={e => setBulkFiles(Array.from(e.target.files))} disabled={capacity.is_full} />
                </label>
                <button data-testid="new-track-btn" onClick={() => { setEditTrack(null); setShowTrackModal(true); }}
                  disabled={capacity.is_full}
                  className="btn-primary py-2 px-5 flex items-center gap-2 text-sm disabled:opacity-50">
                  <Plus className="w-4 h-4" />Nuevo Track
                </button>
              </div>
            </div>

            {/* Bulk Upload Zone */}
            {(bulkFiles.length > 0 || bulkUploaded.length > 0) && (
              <div className="glass-card p-6 mb-6 border border-[#E8720C]/20" data-testid="bulk-upload-zone">
                {bulkUploaded.length === 0 ? (
                  <div>
                    {(() => {
                      const mp3s = bulkFiles.filter(f => f.name.toLowerCase().endsWith('.mp3'));
                      const wavs = bulkFiles.filter(f => f.name.toLowerCase().endsWith('.wav'));
                      const getBase = (n) => n.replace(/\.[^/.]+$/, '').trim().toLowerCase();
                      const paired = [];
                      const unmatchedMp3 = [];
                      const unmatchedWav = [...wavs];
                      mp3s.forEach(mp3 => {
                        const base = getBase(mp3.name);
                        const wIdx = unmatchedWav.findIndex(w => getBase(w.name) === base);
                        if (wIdx >= 0) { paired.push({ mp3, wav: unmatchedWav[wIdx], name: mp3.name.replace(/\.[^/.]+$/, '') }); unmatchedWav.splice(wIdx, 1); }
                        else { unmatchedMp3.push(mp3); }
                      });
                      const allOk = unmatchedMp3.length === 0 && unmatchedWav.length === 0 && paired.length > 0;
                      return (
                        <>
                          <h3 className="font-semibold mb-3">{bulkFiles.length} archivos seleccionados</h3>
                          {paired.length > 0 && (
                            <div className="mb-3">
                              <p className="text-[10px] text-green-400 uppercase tracking-wider font-semibold mb-2">Parejas encontradas ({paired.length})</p>
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {paired.map((p, i) => (
                                  <div key={i} className="flex items-center gap-2 p-2 bg-green-500/5 rounded-lg text-xs">
                                    <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded text-[9px] font-bold">OK</span>
                                    <span className="text-white/70">{p.name}</span>
                                    <span className="text-white/30">MP3 ({(p.mp3.size / 1024 / 1024).toFixed(1)}MB) + WAV ({(p.wav.size / 1024 / 1024).toFixed(1)}MB)</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {(unmatchedMp3.length > 0 || unmatchedWav.length > 0) && (
                            <div className="mb-3 p-3 bg-red-500/5 border border-red-500/20 rounded-xl">
                              <p className="text-[10px] text-red-400 uppercase tracking-wider font-semibold mb-2">Archivos sin pareja</p>
                              {unmatchedMp3.map((f, i) => <div key={`m${i}`} className="text-xs text-red-400/70">MP3 sin WAV: {f.name}</div>)}
                              {unmatchedWav.map((f, i) => <div key={`w${i}`} className="text-xs text-red-400/70">WAV sin MP3: {f.name}</div>)}
                              <p className="text-[10px] text-white/40 mt-2">Cada track necesita un MP3 y un WAV con el mismo nombre.</p>
                            </div>
                          )}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                            <div><label className="block text-[10px] text-white/40 mb-1">BPM (todos)</label>
                              <input type="number" className="w-full input-glass text-xs py-2" placeholder="120"
                                value={batchSettings.bpm} onChange={e => setBatchSettings(p => ({ ...p, bpm: e.target.value }))} /></div>
                            <div><label className="block text-[10px] text-white/40 mb-1">Precio $ (todos)</label>
                              <input type="number" step="0.01" className="w-full input-glass text-xs py-2" placeholder="5.99"
                                value={batchSettings.price} onChange={e => setBatchSettings(p => ({ ...p, price: e.target.value }))} /></div>
                            <div><label className="block text-[10px] text-white/40 mb-1">Genero (todos)</label>
                              <input className="w-full input-glass text-xs py-2" placeholder="Reggaeton..."
                                value={batchSettings.genre} onChange={e => setBatchSettings(p => ({ ...p, genre: e.target.value }))} /></div>
                          </div>
                          <button onClick={() => setBatchSettings(p => ({ ...p, includes_playlist: !p.includes_playlist }))}
                            className={`w-full py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 mb-4 transition-all ${
                              batchSettings.includes_playlist ? 'bg-[#E8720C]/15 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/5 text-white/40 border border-white/10'
                            }`} data-testid="bulk-playlist-toggle">
                            {batchSettings.includes_playlist ? '+ Playlists Activado' : 'Incluir Ecosistema de Playlists'}
                          </button>
                          <div className="flex gap-3">
                            <button onClick={handleBulkUpload} disabled={bulkUploading || !allOk}
                              className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center gap-2" data-testid="bulk-start-upload">
                              {bulkUploading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Subiendo...</> : `Subir ${paired.length} Tracks`}
                            </button>
                            <button onClick={() => setBulkFiles([])} className="px-5 py-2.5 bg-white/10 rounded-xl text-sm text-white/60">Cancelar</button>
                            {!allOk && paired.length === 0 && <p className="text-xs text-red-400 self-center">Selecciona archivos MP3 y WAV con el mismo nombre</p>}
                          </div>
                          {/* Upload Progress Bar */}
                          {bulkUploading && bulkProgress.total > 0 && (
                            <div className="mt-4 p-4 bg-white/[0.03] border border-[#E8720C]/20 rounded-xl" data-testid="bulk-progress">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-white/60 font-medium">Subiendo archivos a S3...</span>
                                <span className="text-sm font-bold text-[#E8720C]">{bulkProgress.current}/{bulkProgress.total}</span>
                              </div>
                              <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden mb-2">
                                <div className="h-full bg-[#E8720C] rounded-full transition-all duration-500" style={{ width: `${Math.round((bulkProgress.current / bulkProgress.total) * 100)}%` }} />
                              </div>
                              <p className="text-[10px] text-white/30">{Math.round((bulkProgress.current / bulkProgress.total) * 100)}% completado — {bulkProgress.total - bulkProgress.current} archivos restantes</p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">{bulkUploaded.length} tracks listos — Edita los datos</h3>
                      <div className="flex gap-3">
                        <button onClick={applyBatchSettings} className="px-3 py-1.5 bg-white/10 rounded-lg text-xs text-white/60 hover:bg-white/15">
                          Aplicar Config a Todos
                        </button>
                        <button onClick={handleBulkCreate} disabled={bulkCreating}
                          className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center gap-2" data-testid="bulk-create-tracks">
                          {bulkCreating ? <><RefreshCw className="w-4 h-4 animate-spin" /> Creando...</> : `Crear ${bulkUploaded.length} Tracks`}
                        </button>
                        <button onClick={() => setBulkUploaded([])} className="px-4 py-2 bg-white/10 rounded-xl text-sm text-white/60">Cancelar</button>
                      </div>
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      <div className="grid grid-cols-5 gap-2 px-3 text-[10px] text-white/40 font-medium uppercase tracking-wider">
                        <span>Archivo</span><span>Titulo</span><span>Precio ($)</span><span>Genero</span><span>BPM</span>
                      </div>
                      {bulkUploaded.map((t, idx) => (
                        <div key={idx} className="grid grid-cols-5 gap-2 p-3 bg-white/[0.03] rounded-xl items-center" data-testid={`bulk-track-${idx}`}>
                          <div className="text-[10px] text-white/50 truncate" title={t.original_filename}>{t.original_filename}</div>
                          <input value={t.title} onChange={e => updateBulkTrack(idx, 'title', e.target.value)}
                            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" />
                          <input type="number" value={t.price} onChange={e => updateBulkTrack(idx, 'price', e.target.value)} placeholder="5.99"
                            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" />
                          <input value={t.genre} onChange={e => updateBulkTrack(idx, 'genre', e.target.value)} placeholder="Genero"
                            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" />
                          <input type="number" value={t.bpm} onChange={e => updateBulkTrack(idx, 'bpm', e.target.value)} placeholder="BPM"
                            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tracks.length === 0 && bulkFiles.length === 0 && bulkUploaded.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Music className="w-10 h-10 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">No tienes tracks aun. Sube tu primera musica.</p>
              </div>
            ) : tracks.length > 0 && (
              <>
                <GenreFilterBar
                  tracks={tracks}
                  selectedCategory={genreCategory}
                  selectedGenre={genreRaw}
                  onSelectCategory={setGenreCategory}
                  onSelectGenre={setGenreRaw}
                />
              <div className="glass-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-white/40 border-b border-white/10">
                      <th className="p-4 w-10"></th><th className="p-4">Titulo</th><th className="p-4">Tag</th>
                      <th className="p-4">Genero</th><th className="p-4">BPM</th><th className="p-4">Estado</th><th className="p-4 text-right">Acciones</th>
                    </tr></thead>
                    <tbody>
                      {tracks.filter(t => {
                        if (genreCategory && categoryForGenre(t.genre) !== genreCategory) return false;
                        if (genreRaw && (t.genre || '').trim() !== genreRaw) return false;
                        return true;
                      }).map(t => {
                        const isDraft = t.approval_status === 'draft' || t.approval_status === 'rejected';
                        return (
                          <tr key={t.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`track-row-${t.id}`}>
                            <td className="p-4">
                              {isDraft && (
                                <input type="checkbox" data-testid={`track-checkbox-${t.id}`}
                                  checked={selectedTracks.has(t.id)} onChange={() => toggleTrackSelection(t.id)}
                                  className="w-4 h-4 accent-[#E8720C]" />
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                {t.cover_image ? (
                                  <img src={`${API_URL}${t.cover_image}`} alt="" className="w-9 h-9 rounded-lg object-cover" />
                                ) : (
                                  <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center"><Music className="w-4 h-4 text-white/20" /></div>
                                )}
                                <span className="font-medium">{t.title}</span>
                              </div>
                            </td>
                            <td className="p-4 text-white/50">{t.artist}</td>
                            <td className="p-4">
                              {editingGenreId === t.id ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    autoFocus
                                    value={genreDraft}
                                    onChange={e => setGenreDraft(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') saveGenre(t); if (e.key === 'Escape') cancelEditGenre(); }}
                                    list={`asst-genre-opts-${t.id}`}
                                    className="px-2 py-1 bg-white/5 border border-[#E8720C]/40 rounded-lg text-xs text-white w-32 focus:outline-none focus:border-[#E8720C]"
                                    placeholder="Género"
                                    data-testid={`genre-input-${t.id}`}
                                  />
                                  <datalist id={`asst-genre-opts-${t.id}`}>
                                    <option value="Afro House" />
                                    <option value="Reggaeton" />
                                    <option value="Dembow" />
                                    <option value="Trap" />
                                    <option value="Trap Latino" />
                                    <option value="Hip-Hop" />
                                    <option value="Drill" />
                                    <option value="R&B" />
                                    <option value="Electronic" />
                                    <option value="House" />
                                    <option value="Tech House" />
                                    <option value="Deep House" />
                                    <option value="Salsa" />
                                    <option value="Bachata" />
                                    <option value="Merengue" />
                                    <option value="Regional Mexicano" />
                                    <option value="Corridos Tumbados" />
                                    <option value="Banda" />
                                    <option value="Norteño" />
                                    <option value="Baladas" />
                                    <option value="Música Cristiana" />
                                    <option value="Gospel" />
                                    <option value="Pop Latino" />
                                    <option value="Pop" />
                                    <option value="Amapiano" />
                                    <option value="Afrobeats" />
                                    <option value="Dancehall" />
                                    <option value="Cumbia" />
                                    <option value="Guaracha" />
                                    <option value="Lo-Fi" />
                                    <option value="Beats Sin Letra" />
                                    <option value="Instrumental" />
                                  </datalist>
                                  <button onClick={() => saveGenre(t)} className="p-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30" data-testid={`genre-save-${t.id}`}>
                                    <Check className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={cancelEditGenre} className="p-1.5 rounded-lg bg-white/5 text-white/50 hover:bg-white/10" data-testid={`genre-cancel-${t.id}`}>
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startEditGenre(t)}
                                  className="px-2 py-1 rounded-full text-xs bg-white/10 hover:bg-[#E8720C]/20 hover:text-[#E8720C] transition-colors"
                                  title="Clic para cambiar género"
                                  data-testid={`genre-edit-btn-${t.id}`}
                                >
                                  {t.genre || '—'}
                                </button>
                              )}
                            </td>
                            <td className="p-4 text-white/50">{t.bpm}</td>
                            <td className="p-4"><StatusBadge status={t.approval_status} /></td>
                            <td className="p-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => togglePlayTrack(t)}
                                  className={`p-2 rounded-lg transition-colors ${playingTrackId === t.id ? 'bg-[#E8720C]/30 text-[#E8720C]' : 'hover:bg-[#E8720C]/15 text-white/60 hover:text-[#E8720C]'}`}
                                  title={playingTrackId === t.id ? 'Pausar' : 'Escuchar preview'}
                                  data-testid={`play-track-${t.id}`}
                                >
                                  {playingTrackId === t.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                </button>
                                {isDraft && (
                                  <>
                                    <button data-testid={`edit-track-${t.id}`} onClick={() => { setEditTrack(t); setShowTrackModal(true); }}
                                      className="p-2 hover:bg-white/10 rounded-lg text-[#E8720C]"><Edit3 className="w-4 h-4" /></button>
                                    <button data-testid={`delete-track-${t.id}`} onClick={() => handleDeleteTrack(t.id)}
                                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              </>
            )}
          </div>
        )}

        {/* ── ARTISTS TAB — Same as Admin (Promocion) ── */}
        {tab === 'artists' && <ArtistsPanelFull />}

        {/* ── SPOTIFY PLAYLISTS TAB ── */}
        {tab === 'spotify-playlists' && (
          <div data-testid="spotify-playlists-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Spotify Playlists</h2>
                <p className="text-white/50 text-sm">Playlists que se muestran en el reproductor de la pagina</p>
              </div>
              <button onClick={() => setSpModal({ name: '', spotify_url: '', description: '', order: spotifyPlaylists.length })}
                className="btn-primary py-2 px-5 flex items-center gap-2 text-sm" data-testid="add-spotify-playlist-btn">
                <Plus className="w-4 h-4" /> Agregar Playlist
              </button>
            </div>

            {spotifyPlaylists.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <ListMusic className="w-10 h-10 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">No hay playlists. Agrega una para el reproductor.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {spotifyPlaylists.map(p => (
                  <div key={p.id} className="glass-card p-4 flex items-center gap-4" data-testid={`spotify-playlist-${p.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-[#1DB954]/15 flex items-center justify-center shrink-0">
                      <ListMusic className="w-5 h-5 text-[#1DB954]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{p.name}</div>
                      <div className="text-xs text-white/40 truncate">{p.spotify_url}</div>
                      {p.description && <div className="text-xs text-white/30 mt-0.5">{p.description}</div>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => setSpModal({ ...p })} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/40">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDeleteSpotifyPlaylist(p.id)} className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {spModal && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSpModal(null)}>
                <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="spotify-playlist-modal">
                  <h3 className="text-lg font-bold mb-4">{spModal.id ? 'Editar' : 'Agregar'} Playlist</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-white/50 mb-1">Nombre</label>
                      <input className="w-full input-glass" value={spModal.name} onChange={e => setSpModal({ ...spModal, name: e.target.value })}
                        placeholder="Reggaeton Hits OG" data-testid="sp-name-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-white/50 mb-1">URL de Spotify</label>
                      <input className="w-full input-glass" value={spModal.spotify_url} onChange={e => setSpModal({ ...spModal, spotify_url: e.target.value })}
                        placeholder="https://open.spotify.com/playlist/5eHfnN93p18Td5qGzIndtZ" data-testid="sp-url-input" />
                      <p className="text-[10px] text-white/30 mt-1">Abre la playlist en Spotify → Compartir → Copiar enlace. Ejemplo: https://open.spotify.com/playlist/5eHfnN93p18Td5qGzIndtZ</p>
                      {spModal.spotify_url && !spModal.spotify_url.includes('open.spotify.com/playlist/') && (
                        <p className="text-[10px] text-red-400 mt-1">El link debe ser de una playlist de Spotify</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-white/50 mb-1">Descripcion (opcional)</label>
                      <input className="w-full input-glass" value={spModal.description || ''} onChange={e => setSpModal({ ...spModal, description: e.target.value })}
                        placeholder="Las mejores del genero..." />
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => handleSaveSpotifyPlaylist(spModal)} className="flex-1 btn-primary py-2.5 flex items-center justify-center gap-2 text-sm" data-testid="sp-save-btn">
                        <Save className="w-4 h-4" />Guardar
                      </button>
                      <button onClick={() => setSpModal(null)} className="px-5 py-2.5 bg-white/10 rounded-xl text-sm text-white/60">Cancelar</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── APPROVALS TAB ── */}
        {tab === 'approvals' && (
          <div data-testid="approvals-section">
            <h2 className="text-2xl font-bold mb-6">Mis Aprobaciones</h2>
            {approvals.length === 0 ? (
              <div className="glass-card p-12 text-center"><p className="text-white/30">No hay aprobaciones aun</p></div>
            ) : (
              <div className="space-y-3">
                {approvals.map(a => (
                  <div key={a.id} data-testid={`approval-${a.id}`} className="glass-card p-5">
                    <div className="flex items-center justify-between mb-3">
                      <StatusBadge status={a.status} />
                      <span className="text-xs text-white/30">{new Date(a.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="space-y-1">
                      {a.tracks_info?.map((t, i) => (
                        <div key={i} className="flex justify-between py-1 border-b border-white/5 text-sm">
                          <span className="text-white/80">{t.title}</span>
                          <span className="text-white/40">{t.artist}</span>
                        </div>
                      ))}
                    </div>
                    {a.admin_notes && (
                      <div className="mt-3 p-3 bg-white/[0.03] rounded-lg border-l-2 border-[#E8720C]">
                        <div className="text-[#E8720C] text-[10px] font-semibold mb-1">Notas del Admin:</div>
                        <div className="text-sm text-white/60">{a.admin_notes}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── NOTIFICATIONS TAB ── */}
        {tab === 'notifications' && (
          <div data-testid="notifications-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Notificaciones</h2>
              <div className="flex gap-2">
                <button data-testid="view-history-btn" onClick={loadHistory}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/50 hover:bg-white/10 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />Historial
                </button>
                {notifications.length > 0 && (
                  <button data-testid="dismiss-all-btn" onClick={() => handleDismissNotifs([])}
                    className="px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400 hover:bg-red-500/20 flex items-center gap-1">
                    <X className="w-3 h-3" />Descartar Todo
                  </button>
                )}
              </div>
            </div>

            {/* ── Notification Detail Modal ── */}
            {selectedNotif && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setSelectedNotif(null); setBuyerHistory(null); }}>
                <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="notif-detail-modal">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">{selectedNotif.title}</h3>
                    <button onClick={() => { setSelectedNotif(null); setBuyerHistory(null); }} className="p-1 rounded-lg hover:bg-white/10 text-white/40"><X className="w-5 h-5" /></button>
                  </div>
                  <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold mb-4 ${
                    selectedNotif.type?.includes('approved') ? 'bg-green-500/20 text-green-400' :
                    selectedNotif.type?.includes('reject') ? 'bg-red-500/20 text-red-400' :
                    selectedNotif.type?.includes('playlist') ? 'bg-purple-500/20 text-purple-400' :
                    'bg-[#E8720C]/20 text-[#E8720C]'
                  }`}>
                    {selectedNotif.type?.includes('approved') ? 'Aprobado' :
                     selectedNotif.type?.includes('reject') ? 'Rechazado' :
                     selectedNotif.type?.includes('playlist') ? 'Playlist' :
                     selectedNotif.type?.includes('edited') ? 'Editado' : 'Notificacion'}
                  </div>
                  <div className="text-sm text-white/70 whitespace-pre-line mb-4">{selectedNotif.message}</div>

                  {/* Extra data details */}
                  {selectedNotif.data && (
                    <div className="space-y-3 border-t border-white/10 pt-4">
                      {selectedNotif.data.user_name && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/40 w-24">Cliente:</span>
                          <span className="text-sm text-white/80">{selectedNotif.data.user_name}</span>
                        </div>
                      )}
                      {selectedNotif.data.user_email && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/40 w-24">Email:</span>
                          <span className="text-sm text-white/80">{selectedNotif.data.user_email}</span>
                        </div>
                      )}
                      {selectedNotif.data.item_name && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/40 w-24">Track:</span>
                          <span className="text-sm text-[#E8720C]">{selectedNotif.data.item_name}</span>
                        </div>
                      )}
                      {selectedNotif.data.platform_name && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/40 w-24">Plataforma:</span>
                          <span className="text-sm text-white/80">{selectedNotif.data.platform_name}</span>
                        </div>
                      )}
                      {selectedNotif.data.link && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/40 w-24">Link:</span>
                          <a href={selectedNotif.data.link} target="_blank" rel="noopener noreferrer"
                            className="text-sm text-[#E8720C] hover:underline truncate" data-testid="notif-spotify-link">
                            {selectedNotif.data.link}
                          </a>
                        </div>
                      )}
                      {selectedNotif.data.client_note && (
                        <div className="mt-2 p-3 bg-white/[0.03] rounded-lg border-l-2 border-purple-500">
                          <div className="text-purple-400 text-[10px] font-semibold mb-1">Nota del Cliente:</div>
                          <div className="text-sm text-white/60">{selectedNotif.data.client_note}</div>
                        </div>
                      )}
                      {selectedNotif.data.approval_id && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/40 w-24">ID Aprobacion:</span>
                          <span className="text-xs text-white/50 font-mono">{selectedNotif.data.approval_id}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Buyer History (Playlist notifications) ── */}
                  {(selectedNotif.type === 'playlist_submission') && (
                    <div className="mt-5 border-t border-white/10 pt-4" data-testid="buyer-history-section">
                      <h4 className="text-sm font-bold text-[#E8720C] mb-3">Historial del Cliente</h4>
                      {buyerHistoryLoading ? (
                        <div className="flex items-center gap-2 text-white/40 text-sm py-4">
                          <div className="w-4 h-4 border-2 border-[#E8720C] border-t-transparent rounded-full animate-spin" />
                          Cargando historial...
                        </div>
                      ) : buyerHistory ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <div className="text-[10px] text-white/40 uppercase">Nombre</div>
                              <div className="text-sm text-white/80">{buyerHistory.user?.name || 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-white/40 uppercase">Email</div>
                              <div className="text-sm text-white/80">{buyerHistory.user?.email || 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-white/40 uppercase">Registrado</div>
                              <div className="text-sm text-white/80">{buyerHistory.user?.created_at ? new Date(buyerHistory.user.created_at).toLocaleDateString() : 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-white/40 uppercase">Pais</div>
                              <div className="text-sm text-white/80">{buyerHistory.user?.country || 'N/A'}</div>
                            </div>
                          </div>
                          <div className="flex gap-4 p-3 bg-white/[0.03] rounded-xl">
                            <div className="text-center flex-1">
                              <div className="text-xl font-bold text-[#E8720C]">{buyerHistory.total_orders}</div>
                              <div className="text-[10px] text-white/40">Compras</div>
                            </div>
                            <div className="text-center flex-1">
                              <div className="text-xl font-bold text-green-400">${buyerHistory.total_spent?.toFixed(2)}</div>
                              <div className="text-[10px] text-white/40">Total Gastado</div>
                            </div>
                            <div className="text-center flex-1">
                              <div className="text-xl font-bold text-purple-400">{buyerHistory.playlist_submissions?.length || 0}</div>
                              <div className="text-[10px] text-white/40">Links Enviados</div>
                            </div>
                          </div>
                          {buyerHistory.purchases?.length > 0 && (
                            <div>
                              <div className="text-[10px] text-white/40 uppercase mb-2">Historial de Compras</div>
                              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {buyerHistory.purchases.map((p, i) => (
                                  <div key={i} className="flex items-center justify-between p-2 bg-white/[0.02] rounded-lg text-xs">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <span className="text-white/70 truncate">{p.item_name}</span>
                                      {p.includes_playlist && <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[9px] shrink-0">Playlist</span>}
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0 ml-2">
                                      <span className="text-white/40">{p.license_type || 'basic'}</span>
                                      <span className="text-green-400 font-medium">${p.amount?.toFixed(2)}</span>
                                      <span className="text-white/30">{p.date ? new Date(p.date).toLocaleDateString() : ''}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {buyerHistory.playlist_submissions?.length > 0 && (
                            <div>
                              <div className="text-[10px] text-white/40 uppercase mb-2">Links de Playlist Enviados</div>
                              <div className="space-y-1.5">
                                {buyerHistory.playlist_submissions.map((s, i) => (
                                  <div key={i} className="flex items-center justify-between p-2 bg-white/[0.02] rounded-lg text-xs">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <span className="text-white/70 truncate">{s.item_name}</span>
                                      <a href={s.platform_link} target="_blank" rel="noopener noreferrer" className="text-[#E8720C] hover:underline truncate text-[10px]">{s.platform_link}</a>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0 ml-2">
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${s.status === 'in_playlist' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                        {s.status === 'in_playlist' ? 'En Playlist' : 'Pendiente'}
                                      </span>
                                      <span className="text-white/30">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-white/30 text-sm">No se pudo cargar el historial</p>
                      )}
                    </div>
                  )}

                  <div className="text-[10px] text-white/20 mt-4">{new Date(selectedNotif.created_at).toLocaleString()}</div>
                </div>
              </div>
            )}

            {showHistory ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[#E8720C] font-semibold">Historial</h3>
                  <button onClick={() => setShowHistory(false)} className="px-3 py-1 bg-white/5 rounded-lg text-xs text-white/40">Volver</button>
                </div>
                {history.length === 0 ? (
                  <p className="text-center text-white/30 py-8">Sin historial</p>
                ) : history.map(n => (
                  <div key={n.id} className="glass-card p-4 mb-2 border-l-2 border-white/10 cursor-pointer hover:bg-white/[0.03] transition-colors"
                    onClick={() => handleNotifClick(n)} data-testid={`history-notif-${n.id}`}>
                    <div className="text-sm font-medium text-white/60">{n.title}</div>
                    <div className="text-xs text-white/40 mt-1 line-clamp-2">{n.message}</div>
                    <div className="text-[10px] text-white/20 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="glass-card p-12 text-center"><p className="text-white/30">No hay notificaciones nuevas</p></div>
            ) : notifications.map(n => (
              <div key={n.id} data-testid={`notif-${n.id}`}
                className={`glass-card p-4 mb-2 flex items-center gap-3 border-l-2 cursor-pointer hover:bg-white/[0.03] transition-colors ${n.type?.includes('approved') ? 'border-green-500' : n.type?.includes('reject') ? 'border-red-500' : n.type?.includes('playlist') ? 'border-purple-500' : 'border-[#E8720C]'}`}
                onClick={() => { setSelectedNotif(n); handleNotifClick(n); }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {!n.read && <div className="w-2 h-2 rounded-full bg-[#E8720C] shrink-0" />}
                    <div className="text-sm font-medium truncate">{n.title}</div>
                  </div>
                  <div className="text-xs text-white/40 mt-0.5 line-clamp-2">{n.message}</div>
                  {n.data?.link && (
                    <div className="text-[10px] text-purple-400 mt-1 truncate">Link: {n.data.link}</div>
                  )}
                  <div className="text-[10px] text-white/20 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                <button data-testid={`dismiss-notif-${n.id}`} onClick={(e) => { e.stopPropagation(); handleDismissNotifs([n.id]); }}
                  className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/30 shrink-0"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}

        {/* ── METRICS TAB ── */}
        {tab === 'metrics' && metrics && (
          <div data-testid="metrics-section">
            <h2 className="text-2xl font-bold mb-6">Mis Metricas</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {[
                { label: 'Total Subidos', value: metrics.uploads.total, color: 'text-[#E8720C]' },
                { label: 'Hoy', value: metrics.uploads.today, color: 'text-blue-400' },
                { label: 'Esta Semana', value: metrics.uploads.week, color: 'text-purple-400' },
                { label: 'Este Mes', value: metrics.uploads.month, color: 'text-green-400' },
                { label: 'Este Ano', value: metrics.uploads.year, color: 'text-yellow-400' },
              ].map((m, i) => (
                <div key={i} data-testid={`metric-card-${i}`} className="glass-card p-5 text-center">
                  <div className={`text-3xl font-bold ${m.color}`}>{m.value}</div>
                  <div className="text-xs text-white/40 mt-1">{m.label}</div>
                </div>
              ))}
            </div>
            <h3 className="text-lg font-semibold mb-4">Estado de Aprobaciones</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Aprobados', value: metrics.approvals.approved, color: 'text-green-400' },
                { label: 'Pendientes', value: metrics.approvals.pending, color: 'text-[#E8720C]' },
                { label: 'Rechazados', value: metrics.approvals.rejected, color: 'text-red-400' },
                { label: 'Borradores', value: metrics.approvals.draft, color: 'text-white/40' },
              ].map((m, i) => (
                <div key={i} data-testid={`approval-metric-${i}`} className="glass-card p-5 text-center">
                  <div className={`text-3xl font-bold ${m.color}`}>{m.value}</div>
                  <div className="text-xs text-white/40 mt-1">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── PACKS TAB (Personal) ── */}
        {tab === 'packs' && (
          <div data-testid="packs-section">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold">Mis Paquetes</h2>
                <p className="text-white/50 text-sm">Crea paquetes con varios tracks</p>
              </div>
              <div className="flex gap-3">
                {selectedPacks.size > 0 && (
                  <button data-testid="submit-pack-approval-btn" onClick={handleSubmitPackApproval} disabled={loading}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-xl text-sm font-semibold text-white flex items-center gap-2">
                    <Send className="w-4 h-4" />Enviar {selectedPacks.size} a Aprobacion
                  </button>
                )}
                <button data-testid="new-pack-btn" onClick={() => { setEditPack(null); setShowPackModal(true); }}
                  className="btn-primary py-2 px-5 flex items-center gap-2 text-sm">
                  <Plus className="w-4 h-4" />Nuevo Paquete
                </button>
              </div>
            </div>
            {packs.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Package className="w-10 h-10 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">No tienes paquetes. Crea tu primer pack.</p>
              </div>
            ) : (
              <div className="glass-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-white/40 border-b border-white/10">
                      <th className="p-4 w-10"></th><th className="p-4">Nombre</th><th className="p-4">Tracks</th>
                      <th className="p-4">Genero</th><th className="p-4">Estado</th><th className="p-4 text-right">Acciones</th>
                    </tr></thead>
                    <tbody>
                      {packs.map(p => {
                        const isDraft = p.approval_status === 'draft' || p.approval_status === 'rejected';
                        return (
                          <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`pack-row-${p.id}`}>
                            <td className="p-4">
                              {isDraft && (
                                <input type="checkbox" checked={selectedPacks.has(p.id)}
                                  onChange={() => { setSelectedPacks(prev => { const n = new Set(prev); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; }); }}
                                  className="w-4 h-4 accent-[#E8720C]" />
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                {p.cover_image ? (
                                  <img src={`${API_URL}${p.cover_image}`} alt="" className="w-9 h-9 rounded-lg object-cover" />
                                ) : (
                                  <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center"><Package className="w-4 h-4 text-white/20" /></div>
                                )}
                                <span className="font-medium">{p.name}</span>
                              </div>
                            </td>
                            <td className="p-4 text-white/50">{p.track_count}</td>
                            <td className="p-4"><span className="px-2 py-1 rounded-full text-xs bg-white/10">{p.genre}</span></td>
                            <td className="p-4"><StatusBadge status={p.approval_status} /></td>
                            <td className="p-4 text-right">
                              {isDraft && (
                                <div className="flex items-center justify-end gap-1">
                                  <button onClick={() => { setEditPack(p); setShowPackModal(true); }}
                                    className="p-2 hover:bg-white/10 rounded-lg text-[#E8720C]"><Edit3 className="w-4 h-4" /></button>
                                  <button onClick={() => handleDeletePack(p.id)}
                                    className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════ */}
        {/* ═══ FULL ACCESS MODULES (with hideFinancials) ═══ */}
        {/* ══════════════════════════════════════════════════ */}
        {tab === 'f-tracks' && <TracksPanelFull hideFinancials={true} />}
        {tab === 'f-packs' && <PacksPanelFull hideFinancials={true} />}
        {tab === 'f-orders' && <OrdersPanel hideFinancials={true} />}
        {tab === 'f-deliveries' && <DeliveryPanel />}
        {tab === 'f-support' && <SupportPanel />}
        {tab === 'f-replacements' && <ReplacementPanel />}
        {tab === 'f-production' && <ProductionPanel />}
        {tab === 'f-mastering' && <MasteringStaffPanel />}
        {tab === 'f-ultimate' && <UltimateRequestsPanel />}
        {tab === 'f-news' && <NewsPanel />}
        {tab === 'f-leads' && <LeadsPanel />}
        {tab === 'f-satisfaction' && <SatisfactionPanelShared />}
        {tab === 'f-geography' && <GeographyPanel hideFinancials={true} />}
        {tab === 'f-plans' && <PlansPanel hideFinancials={true} />}
        {tab === 'f-campaigns' && (
          <div data-testid="assistant-campaigns-content">
            <div className="flex gap-1 mb-6 bg-white/[0.03] p-1 rounded-xl">
              <button onClick={() => setAsstCampSubTab('promotions')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${asstCampSubTab === 'promotions' ? 'bg-[#E8720C] text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}>
                <Tag className="w-3.5 h-3.5" /> Ofertas
              </button>
              <button onClick={() => setAsstCampSubTab('marketing')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${asstCampSubTab === 'marketing' ? 'bg-[#E8720C] text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}>
                <Image className="w-3.5 h-3.5" /> Estudio Marketing
              </button>
              <button onClick={() => setAsstCampSubTab('campaigns')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${asstCampSubTab === 'campaigns' ? 'bg-[#E8720C] text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}>
                <Mail className="w-3.5 h-3.5" /> Campanas Email
              </button>
            </div>
            {asstCampSubTab === 'promotions' && <PromotionsPanel />}
            {asstCampSubTab === 'campaigns' && <CampaignsPanelV2 />}
            {asstCampSubTab === 'marketing' && <MarketingStudio />}
          </div>
        )}
        {tab === 'f-email' && <EmailControlPanel />}
      </main>

      {/* Modals */}
      {showTrackModal && (
        <TrackModal track={editTrack} artists={artists} onClose={() => { setShowTrackModal(false); setEditTrack(null); }} onSave={handleSaveTrack} />
      )}
      {showArtistModal && (
        <ArtistModal artist={editArtist} onClose={() => { setShowArtistModal(false); setEditArtist(null); }} onSave={handleSaveArtist} />
      )}
      {showPackModal && (
        <PackModalAssistant pack={editPack} tracks={tracks} onClose={() => { setShowPackModal(false); setEditPack(null); }} onSave={handleSavePack} />
      )}
    </div>
  );
};

export default AssistantPage;
